import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { apiUrl, ensureApiConfig } from '../lib/apiBase';

const inquiryOptions = [
  '— Select —',
  'Color Formulation',
  'White Masterbatch',
  'UV / Additive MB',
  'NIR Absorber',
  'Technical Consultation',
  'Partnership',
  'Trend Intelligence Report',
];

const polymerOptions = [
  '— Select —',
  'PET / rPET',
  'PP / BOPP',
  'PE (HDPE / LDPE)',
  'ABS / PS / PC',
  'Engineering Polymers',
  'Bioplastics (PLA / PBAT)',
  'Not Sure',
];

export default function ContactForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [inquiryType, setInquiryType] = useState(inquiryOptions[0]);
  const [polymerType, setPolymerType] = useState(polymerOptions[0]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    await ensureApiConfig();
    const endpoint = apiUrl('/api/contact');
    try {
      const payload = {
        firstName,
        lastName,
        company,
        email,
        inquiryType: inquiryType === inquiryOptions[0] ? '' : inquiryType,
        polymerType: polymerType === polymerOptions[0] ? '' : polymerType,
        message,
      };
      const { data } = await axios.post(endpoint, payload, {
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' },
      });
      if (data?.ok) {
        const dbHint =
          ' Set MYSQL_HOST, MYSQL_USER, MYSQL_PASSWORD, and MYSQL_DATABASE in server/.env, then restart the API (cd server → npm start).';
        if (data.stored) {
          if (data.emailSent) {
            toast.success(
              'Inquiry saved. A confirmation email is on its way to your inbox.'
            );
          } else {
            toast.success(
              'Inquiry saved. We will respond soon. (Add SMTP in server/.env to email a confirmation.)'
            );
          }
        } else if (data.emailSent) {
          toast.warn(
            `Confirmation email was sent, but the inquiry was not saved — MySQL is not active on the server.${dbHint}`
          );
        } else {
          toast.warn(
            `${data.note || 'Inquiry was not saved to the database.'}${dbHint}`
          );
        }
      } else {
        toast.error('Could not send inquiry. Please try again.');
      }
    } catch (err) {
      let msg = err?.response?.data?.error || err.message || 'Request failed';
      if (err.response?.status === 404) {
        msg =
          `API not found (${endpoint}). Production: run the Node server with client/build, or set REACT_APP_API_URL at build time, or add config.json with "apiUrl" (see client/public/config.json.example).`;
      } else if (!err.response) {
        const code = err.code || '';
        const isNetwork =
          code === 'ERR_NETWORK' ||
          code === 'ECONNABORTED' ||
          (typeof err.message === 'string' && err.message.toLowerCase().includes('network'));
        if (isNetwork) {
          msg = `Cannot reach the API (${endpoint}). Start the backend: cd server → npm start (port 5000), keep this site on npm start in client. Or set REACT_APP_API_URL to your API base.`;
        }
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="rr" onSubmit={onSubmit}>
      <div className="frow">
        <div className="fld">
          <label htmlFor="fn">First Name</label>
          <input
            id="fn"
            type="text"
            placeholder="John"
            required
            value={firstName}
            onChange={(ev) => setFirstName(ev.target.value)}
          />
        </div>
        <div className="fld">
          <label htmlFor="ln">Last Name</label>
          <input
            id="ln"
            type="text"
            placeholder="Doe"
            required
            value={lastName}
            onChange={(ev) => setLastName(ev.target.value)}
          />
        </div>
      </div>
      <div className="fld">
        <label htmlFor="co">Company</label>
        <input
          id="co"
          type="text"
          placeholder="Your organization"
          required
          value={company}
          onChange={(ev) => setCompany(ev.target.value)}
        />
      </div>
      <div className="fld">
        <label htmlFor="em">Email</label>
        <input
          id="em"
          type="email"
          placeholder="john@company.com"
          required
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
        />
      </div>
      <div className="frow">
        <div className="fld">
          <label htmlFor="inq">Inquiry Type</label>
          <select id="inq" value={inquiryType} onChange={(ev) => setInquiryType(ev.target.value)}>
            {inquiryOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="pol">Polymer Type</label>
          <select id="pol" value={polymerType} onChange={(ev) => setPolymerType(ev.target.value)}>
            {polymerOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="fld">
        <label htmlFor="msg">Your Message</label>
        <textarea
          id="msg"
          rows={5}
          placeholder="Describe your application, processing conditions, volume requirements, and any regulatory constraints..."
          value={message}
          onChange={(ev) => setMessage(ev.target.value)}
        />
      </div>
      <div className="f-foot">
        <span className="f-note">We respond within 24 business hours.</span>
        <button type="submit" className="btn-p" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send Inquiry →'}
        </button>
      </div>
    </form>
  );
}
